/**
 * PoReconciliationPanel — one page per Supplier PO: ordered vs invoiced vs
 * paid vs remaining, the invoices and payments behind it, and a clean close.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  ClipboardCheck, Loader2, FileText, ChevronLeft, Lock, Unlock, AlertTriangle, Search,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { API } from "@/App";
import { openAuthedDoc } from "@/lib/openAuthedDoc";

const money = (v) => `$${(Number(v) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const STATE_PILL = {
  open: "bg-slate-100 text-slate-700 ring-slate-200",
  partially_invoiced: "bg-sky-50 text-sky-800 ring-sky-200",
  fully_invoiced: "bg-indigo-50 text-indigo-800 ring-indigo-200",
  over_billed: "bg-red-50 text-red-700 ring-red-200",
  paid: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  closed: "bg-slate-800 text-white ring-slate-800",
};
const STATE_LABEL = {
  open: "open", partially_invoiced: "part invoiced", fully_invoiced: "fully invoiced",
  over_billed: "over-billed", paid: "paid", closed: "closed",
};

export default function PoReconciliationPanel({ token, refreshKey = 0 }) {
  const headers = { Authorization: `Bearer ${token}` };
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState(null);
  const [states, setStates] = useState([]);
  const [state, setState] = useState("all");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/po-reconciliation`, { headers, params: { state, q: q || undefined } });
      setRows(r.data.pos || []);
      setTotals(r.data.totals || null);
      setStates(r.data.states || []);
    } catch { /* quiet */ } finally { setLoading(false); }
  }, [state, q, token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load, refreshKey]);

  const openPo = async (id) => {
    try {
      const r = await axios.get(`${API}/po-reconciliation/${id}`, { headers });
      setOpen(r.data);
    } catch { toast.error("Could not open that PO"); }
  };

  const close = async () => {
    const ties = Math.abs(open.remaining_to_invoice) <= 0.01 && Math.abs(open.remaining_to_pay) <= 0.01;
    let reason = "";
    if (!ties) {
      reason = window.prompt(
        `This PO does not tie out — ${money(open.invoiced)} invoiced and ${money(open.paid)} paid against ${money(open.ordered)} ordered.\nWhy are you closing it?`) || "";
      if (!reason.trim()) return;
    }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/po-reconciliation/${open.po.id}/close`, { reason }, { headers });
      setOpen(r.data);
      toast.success(r.data.tied_out ? "PO closed — tied out in full" : "PO closed with your reason on record");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not close the PO"); } finally { setBusy(false); }
  };

  const reopen = async () => {
    const reason = window.prompt("Why are you reopening this PO?") || "";
    if (!reason.trim()) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/po-reconciliation/${open.po.id}/reopen`, { reason }, { headers });
      setOpen(r.data);
      toast.success("PO reopened");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not reopen the PO"); } finally { setBusy(false); }
  };

  // ------------------------------------------------------------- detail view
  if (open) {
    const po = open.po || {};
    const hosp = po.hospital || {};
    return (
      <section className="rounded-xl border border-slate-200 bg-white p-4" data-testid="po-recon-detail">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-start gap-2">
            <Button size="sm" variant="ghost" onClick={() => { setOpen(null); load(); }} data-testid="po-recon-back">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <div>
              <p className="text-[10px] font-black uppercase tracking-[0.14em] text-indigo-700">
                PO reconciliation
              </p>
              <h3 className="text-base font-black text-slate-900">
                {po.po_number || po.id}
                <span className={`ml-2 text-[9px] font-black uppercase tracking-wide px-2 py-0.5 rounded-full ring-1 ${STATE_PILL[open.state]}`}
                  data-testid="po-recon-state">
                  {STATE_LABEL[open.state] || open.state}
                </span>
              </h3>
              <p className="text-xs text-slate-500">
                {po.supplier_name}
                {hosp.hospital_name ? ` · ${hosp.hospital_name}` : ""}
                {hosp.hospital_po ? ` · hospital PO ${hosp.hospital_po}` : ""}
                {po.issue_date ? ` · issued ${po.issue_date}` : ""}
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" data-testid="po-recon-pdf"
              onClick={() => openAuthedDoc(`/po-reconciliation/${po.id}/summary`, { token, fmt: "pdf" })}>
              <FileText className="w-3.5 h-3.5 mr-1" /> Summary PDF
            </Button>
            {open.state === "closed" ? (
              <Button size="sm" variant="outline" onClick={reopen} disabled={busy} data-testid="po-recon-reopen">
                <Unlock className="w-3.5 h-3.5 mr-1" /> Reopen
              </Button>
            ) : (
              <Button size="sm" className="bg-slate-900 hover:bg-slate-800" onClick={close} disabled={busy}
                data-testid="po-recon-close">
                {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Lock className="w-3.5 h-3.5 mr-1" />}
                Close PO
              </Button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-5 gap-2 mt-3" data-testid="po-recon-maths">
          {[
            ["Ordered", money(open.ordered), ""],
            ["Invoiced", money(open.invoiced), open.over_billed_by ? "text-red-700" : ""],
            ["Paid", money(open.paid), "text-emerald-700"],
            ["Left to invoice", money(open.remaining_to_invoice), ""],
            ["Left to pay", money(open.remaining_to_pay), "text-amber-700"],
          ].map(([k, v, cls]) => (
            <div key={k} className="rounded-lg border border-slate-200 p-2.5">
              <p className="text-[9px] font-black uppercase tracking-wider text-slate-400">{k}</p>
              <p className={`text-sm font-black ${cls || "text-slate-900"}`}>{v}</p>
            </div>
          ))}
        </div>

        {!!open.over_billed_by && (
          <p className="mt-2 text-[12px] font-bold text-red-700 bg-red-50 border border-red-200 rounded p-2"
            data-testid="po-recon-over">
            <AlertTriangle className="w-3.5 h-3.5 inline mr-1" />
            Over-billed by {money(open.over_billed_by)} against this PO.
          </p>
        )}
        {!!(open.duplicate_warnings || []).length && (
          <div className="mt-2 text-[12px] text-red-700 bg-red-50 border border-red-200 rounded p-2"
            data-testid="po-recon-dupes">
            <p className="font-bold">
              <AlertTriangle className="w-3.5 h-3.5 inline mr-1" /> Possible duplicate billing
            </p>
            {open.duplicate_warnings.map((w, i) => (
              <p key={i} className="text-[11.5px]">· {w.detail} (invoice {w.invoice_number})</p>
            ))}
          </div>
        )}

        <h4 className="text-[10px] font-black uppercase tracking-wider text-slate-500 mt-4 mb-1">
          Invoices billed ({(open.invoices || []).length})
        </h4>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px] text-sm">
            <thead className="bg-slate-50">
              <tr>
                {["Invoice #", "Date", "Status", "Billed", "Paid", "Balance"].map((h) => (
                  <th key={h} className="text-left px-2 py-1.5 text-[9.5px] font-black uppercase tracking-wider text-slate-500">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {(open.invoices || []).map((i) => (
                <tr key={i.id} data-testid={`po-recon-invoice-${i.id}`}>
                  <td className="px-2 py-1.5 text-[11px] font-mono font-bold">
                    {i.invoice_number}
                    {!!i.duplicate_warnings.length && (
                      <AlertTriangle className="w-3 h-3 inline ml-1 text-red-600" />
                    )}
                  </td>
                  <td className="px-2 py-1.5 text-[11px]">{i.invoice_date || "—"}</td>
                  <td className="px-2 py-1.5 text-[11px]">{i.status}</td>
                  <td className="px-2 py-1.5 text-[11px]">{money(i.amount)}</td>
                  <td className="px-2 py-1.5 text-[11px] text-emerald-700">{money(i.paid)}</td>
                  <td className="px-2 py-1.5 text-[11px] font-black">{money(i.balance)}</td>
                </tr>
              ))}
              {!(open.invoices || []).length && (
                <tr><td colSpan={6} className="px-2 py-4 text-center text-sm text-slate-400">
                  Nothing billed against this PO yet.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>

        <h4 className="text-[10px] font-black uppercase tracking-wider text-slate-500 mt-4 mb-1">
          Payments applied ({(open.payments || []).length})
        </h4>
        {!(open.payments || []).length ? (
          <p className="text-sm text-slate-400">Nothing paid against this PO yet.</p>
        ) : (
          <div className="divide-y divide-slate-100">
            {open.payments.map((p) => (
              <div key={p.payment_id} className="py-1.5" data-testid={`po-recon-payment-${p.payment_id}`}>
                <p className="text-[12px] font-bold text-slate-900">
                  {money(p.amount)} · {p.method} · {p.payment_date || "—"}
                </p>
                <p className="text-[11px] text-slate-500">
                  {p.reference ? `ref ${p.reference}` : "no confirmation number"}
                  {p.bank_name ? ` · ${p.bank_name}${p.account_last4 ? ` ••••${p.account_last4}` : ""}` : ""}
                  {" · "}{p.invoices.map((x) => `${x.invoice_number} ${money(x.amount)}`).join(", ")}
                </p>
              </div>
            ))}
          </div>
        )}

        {po.status === "closed" && (
          <p className="mt-3 text-[11.5px] text-slate-600 bg-slate-50 border border-slate-200 rounded p-2"
            data-testid="po-recon-closed-note">
            Closed {(po.closed_at || "").slice(0, 10)} by {po.closed_by} — {po.closed_reason}
          </p>
        )}
      </section>
    );
  }

  // --------------------------------------------------------------- list view
  return (
    <section className="rounded-xl border border-slate-200 bg-white p-4" data-testid="po-recon-panel">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="text-[10px] font-black uppercase tracking-[0.14em] text-indigo-700">Reconciliation</p>
          <h3 className="text-base font-black text-slate-900 inline-flex items-center gap-2">
            <ClipboardCheck className="w-4 h-4 text-indigo-700" /> Supplier PO reconciliation
          </h3>
          <p className="text-xs text-slate-500">
            Ordered against invoiced against paid, PO by PO — then close it out.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2 top-2.5 text-slate-400" />
            <Input className="h-8 w-44 pl-7 text-xs" placeholder="PO # or supplier" value={q}
              onChange={(e) => setQ(e.target.value)} data-testid="po-recon-search" />
          </div>
          <select value={state} className="h-8 text-xs border border-slate-300 rounded-md px-2"
            onChange={(e) => setState(e.target.value)} data-testid="po-recon-state-filter">
            {states.map((s) => <option key={s} value={s}>{STATE_LABEL[s] || s}</option>)}
          </select>
        </div>
      </div>

      {totals && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-2 mt-3" data-testid="po-recon-totals">
          {[
            ["Ordered", money(totals.ordered)],
            ["Invoiced", money(totals.invoiced)],
            ["Paid", money(totals.paid)],
            ["Still to pay", money(totals.remaining_to_pay)],
            ["Over-billed POs", `${totals.over_billed}`],
          ].map(([k, v]) => (
            <div key={k} className="rounded-lg border border-slate-200 p-2.5">
              <p className="text-[9px] font-black uppercase tracking-wider text-slate-400">{k}</p>
              <p className="text-sm font-black text-slate-900">{v}</p>
            </div>
          ))}
        </div>
      )}

      <div className="overflow-x-auto mt-3">
        <table className="w-full min-w-[760px] text-sm">
          <thead className="bg-slate-50">
            <tr>
              {["PO / supplier", "Ordered", "Invoiced", "Paid", "Left to pay", "Status", ""].map((h, i) => (
                <th key={`${h}-${i}`} className="text-left px-2 py-1.5 text-[9.5px] font-black uppercase tracking-wider text-slate-500">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? (
              <tr><td colSpan={7} className="px-2 py-5 text-center text-sm text-slate-400">
                <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading…
              </td></tr>
            ) : !rows.length ? (
              <tr><td colSpan={7} className="px-2 py-5 text-center text-sm text-slate-400"
                data-testid="po-recon-empty">
                No purchase orders match that filter.
              </td></tr>
            ) : rows.map((r) => (
              <tr key={r.id} data-testid={`po-recon-row-${r.id}`}
                className={r.over_billed_by ? "bg-red-50/50" : ""}>
                <td className="px-2 py-2">
                  <p className="text-[12px] font-bold text-slate-900 font-mono">
                    {r.po_number || "no number"}
                    {!!r.duplicate_warnings && (
                      <AlertTriangle className="w-3 h-3 inline ml-1 text-red-600" title="Possible duplicate billing" />
                    )}
                  </p>
                  <p className="text-[11px] text-slate-500">
                    {r.supplier_name} · {r.invoice_count} invoice(s)
                  </p>
                </td>
                <td className="px-2 py-2 text-[11px]">{money(r.ordered)}</td>
                <td className={`px-2 py-2 text-[11px] ${r.over_billed_by ? "text-red-700 font-bold" : ""}`}>
                  {money(r.invoiced)}
                </td>
                <td className="px-2 py-2 text-[11px] text-emerald-700">{money(r.paid)}</td>
                <td className="px-2 py-2 text-[11px] font-black">{money(r.remaining_to_pay)}</td>
                <td className="px-2 py-2">
                  <span className={`text-[9px] font-black uppercase tracking-wide px-2 py-0.5 rounded-full ring-1 ${STATE_PILL[r.state]}`}>
                    {STATE_LABEL[r.state] || r.state}
                  </span>
                </td>
                <td className="px-2 py-2 text-right">
                  <Button size="sm" variant="outline" className="h-7" onClick={() => openPo(r.id)}
                    data-testid={`po-recon-open-${r.id}`}>Open</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
